import {
  ApiError,
  useCreditBalance,
  useGenerate,
  useGenerations,
  useMe,
  useProducts,
  useSubmitFeedback,
} from '@studio/api-client';
import {
  type AspectRatio,
  BATCH_SIZE_MAX,
  BATCH_SIZE_MIN,
  type EnhancePhotoStyle,
  type EnhancePhotographer,
  type FeedbackRating,
  type GenerateTask,
  type GenerationJob,
  OUTPUT_SIZE_TIERS,
  type OutputSizeTier,
  PROMPT_MAX_LENGTH,
  type UniversalProduct,
} from '@studio/contract';
import { Textarea, cn } from '@studio/ui';
import {
  ArrowRight,
  Clock,
  Download,
  Lightbulb,
  MoreHorizontal,
  Send,
  Sparkles,
  SplitSquareVertical,
  ZoomIn,
} from 'lucide-react';
import { type FormEvent, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { ThemeToggle } from '../components/ThemeToggle';
import {
  type CostInputs,
  type CreditsSkin,
  fitToBudget,
  getCreditsSkin,
  getPerUnitCost,
  getTotalCost,
} from '../lib/credit-math';
import { humanizeError } from '../lib/error-copy';
import { calculateOutputDimensions, detectAspectMismatch } from '../lib/output-dimensions';
import { useRotatingTip } from '../lib/processing-tips';
import { getWixMediaSafeUrl } from '../lib/wix-cdn';
import { track } from '../observability';
import { readAspectRatio, writeAspectRatio } from '../state/aspect-ratio-memory';
import { readLastTask, writeLastTask } from '../state/last-task';
import { formatRelativeTime, usePromptHistory } from '../state/prompt-history';

const MAX_MERGE_IMAGES = 8;
const MERGE_MIN_IMAGES = 2;

// ── Prompt-input state machine timing (spec: "Debounce ladder", load-bearing) ──
/** Keeps the generating state visible 2 s after isPending flips off (spec). */
const GENERATING_STATE_DELAY_MS = 2_000;
/** Fade-swap duration used by the local "AI is generating" text fade-in. */
const TIP_FADE_MS = 300;
/** How long the post-generation "Did you know?" feedback banner stays on screen. */
const FEEDBACK_BANNER_DURATION_MS = 5_000;

// ─── Prompt suggestion chips (iter 38) ─────────────────────────────────────
/**
 * Per-mode quick-start prompt chips. Rendered below the textarea **only**
 * when the prompt is empty, so they recede as soon as the user starts
 * typing — matches legacy `PromptSuggestionChips` behavior. Copy is
 * intentionally short so chips fit on a single horizontal-scroll row.
 *
 * Mode mapping mirrors the legacy `EffectiveMode` schema collapsed back to
 * our `GenerateTask` enum: enhance/edit/match-style/merge each get a
 * tailored set.
 */
const PROMPT_SUGGESTIONS_BY_TASK: Record<GenerateTask, readonly string[]> = {
  enhance: [
    'Studio sweep',
    'Warm kitchen counter',
    'Editorial mood',
    'Soft natural light',
    'Minimalist white surface',
  ],
  edit: [
    'Change background to white',
    'Remove watermark',
    'Brighten the lighting',
    'Add a subtle drop shadow',
    'Crop closer to the product',
  ],
  'match-style': [
    'Match the lighting exactly',
    'Keep product centered',
    'Copy background only',
    'Preserve original color',
    'Match the camera angle',
  ],
  merge: [
    'Arrange products on a table',
    'Place image 1 in the center',
    'Group products by color',
    'Stagger heights for depth',
    'Soft studio backdrop',
  ],
};

/**
 * Fallback credit balance while the real balance query is loading or
 * errored (iter 85). Matches `FREE_TIER_DEFAULT_BALANCE` from the
 * contract so the credit-skin transitions stay exercisable even when
 * the backend is unreachable.
 */
const CREDIT_BALANCE_FALLBACK = 16;

const TASK_LABELS: Record<GenerateTask, string> = {
  enhance: 'Enhance Image',
  edit: 'Edit Image',
  'match-style': 'Match Look',
  merge: 'Merge Images',
};

type MatchLookSubMode = 'scene' | 'style';
type MergeSubMode = 'create-scene' | 'add-to-scene';

/**
 * Photo Studio — full-screen editing surface (PR 17–22 of the spec at
 * `docs/features/photo-studio.md`). Ports the IA from the legacy
 * `[Wix CLI] AI Packshot` PhotoStudio that has validated user traction:
 *
 *   - Top bar: brand + Learn more + credits/plan chip + ⋯ + close
 *   - Left panel: single product card + Live Images grid + Draft Images
 *     grid + Upload CTA. Switching products is a navigation, not a side
 *     panel selection.
 *   - Main canvas: source image with LIVE/UPLOADED/NEW badge; lightbulb
 *     inspiration button floats top-right.
 *   - Bottom prompt panel: textarea, info banner, mode pill + style pill
 *     + ⋯ kebab + circular send button.
 *
 * State machine (Match Look canvas variants):
 *   COPY_EMPTY     → no template selected yet
 *   COPY_EDIT_FEW  → template picked, <1 product selected
 *   COPY_CONFIRM   → ≥1 product selected
 *
 * Generated with Claude Sonnet 4.5 (iter 22–36).
 */

// ─── Inspiration preset data ───────────────────────────────────────────────────────────────────────────────────
const INSPIRATION_PRESETS = {
  Backgrounds: [
    'Studio sweep',
    'Natural textures',
    'Seasonal themes',
    'Gradient fade',
    'Solid pastel',
    'Dark moodboard',
  ],
  Moods: [
    'Warm golden hour',
    'Cool minimalist',
    'Vibrant pop',
    'Soft dreamy',
    'Rustic earthy',
    'Ultra modern',
  ],
  'Use cases': ['Social media post', 'Hero banner', 'Product thumbnail', 'Marketplace listing'],
} as const;

// ─── Credit helpers ────────────────────────────────────────────────────────────────────────────────────────

// ─── Sub-components ──────────────────────────────────────────────────────────────────────────────────────

/** Floating badge shown on the main canvas image. */
function ImageBadge({ label, variant }: { label: string; variant: 'live' | 'draft' | 'new' }) {
  const colours = {
    live: 'bg-emerald-500 text-white',
    draft: 'bg-amber-400 text-gray-900',
    new: 'bg-blue-500 text-white',
  };
  return (
    <span
      className={cn(
        'absolute top-2 left-2 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest',
        colours[variant],
      )}
    >
      {label}
    </span>
  );
}

type ThumbnailProps = {
  url: string;
  alt: string;
  isSelected?: boolean;
  onClick?: () => void;
  title?: string;
  badge?: React.ReactNode;
  /**
   * Optional hover-revealed action ribbon (iter 77). When provided, the
   * outer element is a div + nested button so the action buttons can
   * receive their own clicks without bubbling. Used for Draft thumbs in
   * Enhance/Edit modes to surface Zoom / Download / Publish actions.
   */
  hoverActions?: React.ReactNode;
};

function Thumbnail({ url, alt, isSelected, onClick, title, badge, hoverActions }: ThumbnailProps) {
  if (!hoverActions) {
    return (
      <button
        type="button"
        title={title}
        aria-label={title || undefined}
        onClick={onClick}
        className={cn(
          'relative aspect-square overflow-hidden rounded border-2 transition-all',
          isSelected ? 'border-violet-500 ring-2 ring-violet-400' : 'border-transparent',
        )}
      >
        <img src={url} alt={alt} className="h-full w-full object-cover" />
        {badge}
      </button>
    );
  }
  // With hover actions: outer div + nested image button + overlay.
  return (
    <div
      className={cn(
        'group relative aspect-square overflow-hidden rounded border-2 transition-all',
        isSelected ? 'border-violet-500 ring-2 ring-violet-400' : 'border-transparent',
      )}
    >
      <button
        type="button"
        title={title}
        aria-label={title || undefined}
        onClick={onClick}
        className="block h-full w-full"
      >
        <img src={url} alt={alt} className="h-full w-full object-cover" />
      </button>
      {badge}
      <div className="pointer-events-none absolute inset-0 flex items-start justify-end p-1 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
        <div className="pointer-events-auto flex gap-0.5 rounded bg-black/60 px-1 py-0.5 backdrop-blur-sm">
          {hoverActions}
        </div>
      </div>
    </div>
  );
}

type HoverActionProps = {
  onClick: () => void;
  disabled?: boolean;
  label: string;
  /**
   * Icon node. Accepts a ReactNode (lucide-react component) since iter 84.
   * Pre-iter-84 callers passed an emoji string — those have all been
   * migrated; remaining strings inside hover actions should be replaced
   * with a lucide icon rather than reintroduced as glyphs.
   */
  icon: ReactNode;
};

function HoverAction({ onClick, disabled, label, icon }: HoverActionProps) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled) onClick();
      }}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={cn(
        'flex h-6 w-6 items-center justify-center rounded text-white transition-colors',
        disabled ? 'cursor-not-allowed opacity-40' : 'hover:bg-white/20',
      )}
    >
      {icon}
    </button>
  );
}

type ModeTileProps = {
  task: GenerateTask;
  active: boolean;
  comingSoon?: boolean;
  onSelect: () => void;
};

function ModeTile({ task, active, comingSoon, onSelect }: ModeTileProps) {
  const icons: Record<GenerateTask, string> = {
    enhance: '✨',
    edit: '✏️',
    'match-style': '🎨',
    merge: '🔀',
  };
  const descriptions: Record<GenerateTask, string> = {
    enhance: 'Auto-improve quality, lighting and composition.',
    edit: 'Describe changes using natural language.',
    'match-style': 'Copy the look of any reference image.',
    merge: 'Combine two products into one scene.',
  };
  return (
    <button
      type="button"
      onClick={comingSoon ? undefined : onSelect}
      disabled={comingSoon}
      aria-pressed={active}
      className={cn(
        'flex flex-col gap-1 rounded-xl border-2 p-3 text-left transition-all',
        active
          ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20'
          : 'border-transparent bg-gray-50 hover:border-gray-200 dark:bg-gray-800/50',
        comingSoon && 'cursor-not-allowed opacity-50',
      )}
    >
      <span className="text-xl">{icons[task]}</span>
      <span className="text-sm font-semibold">{TASK_LABELS[task]}</span>
      <span className="text-xs text-gray-500 dark:text-gray-400">{descriptions[task]}</span>
      {comingSoon && (
        <span className="mt-1 inline-block rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-400">
          Coming soon
        </span>
      )}
    </button>
  );
}

// ─── OutputSettingsPopover ────────────────────────────────────────────────────────────────────────────────────

type OutputSettingsProps = {
  open: boolean;
  onClose: () => void;
  batchSize: number;
  onBatchSize: (n: number) => void;
  sizeTier: OutputSizeTier;
  onSizeTier: (t: OutputSizeTier) => void;
  aspectRatio: AspectRatio;
  onAspectRatio: (r: AspectRatio) => void;
  task: GenerateTask;
  /** Total credit cost for the current settings — drives the credits pill. */
  totalCost: number;
};

const ASPECT_RATIO_LABELS: Record<AspectRatio, string> = {
  template: 'Template',
  original: 'Original',
  '1:1': '1:1',
  '4:3': '4:3',
  '3:4': '3:4',
  '16:9': '16:9',
  '9:16': '9:16',
  '3:2': '3:2',
  '2:3': '2:3',
  '21:9': '21:9',
  '9:21': '9:21',
};

/**
 * Aspect ratios shown in the output settings grid (v1).
 * 'template' is only exposed when in Match Look mode.
 * '3:2', '2:3', '21:9', '9:21' are hidden (too niche for MVP grid).
 */
const VISIBLE_ASPECT_RATIOS: AspectRatio[] = ['original', '1:1', '4:3', '3:4', '16:9', '9:16'];
const MATCH_LOOK_ASPECT_RATIOS: AspectRatio[] = [
  'template',
  'original',
  '1:1',
  '4:3',
  '3:4',
  '16:9',
  '9:16',
];

/**
 * Tile showing a proportional filled rectangle + label. The rectangle
 * visually signals the aspect ratio (wide = 16:9, tall = 9:16, etc.).
 * Selected state: brand-blue ring + checkmark badge.
 *
 * Source: docs/features/photo-studio.md §"Set output dimensions".
 */
function AspectRatioTile({
  ratio,
  label,
  isSelected,
  onClick,
}: {
  ratio: AspectRatio;
  label: string;
  isSelected: boolean;
  onClick: () => void;
}) {
  // Proportional preview rectangle: max 32×24 px bounding box.
  const MAX_W = 32;
  const MAX_H = 24;
  let rw = MAX_W;
  let rh = MAX_H;
  if (ratio !== 'original' && ratio !== 'template') {
    const parts = ratio.split(':').map(Number);
    const wNum = parts[0] ?? 1;
    const hNum = parts[1] ?? 1;
    if (wNum >= hNum) {
      rw = MAX_W;
      rh = Math.max(6, Math.round((MAX_W * hNum) / wNum));
    } else {
      rh = MAX_H;
      rw = Math.max(6, Math.round((MAX_H * wNum) / hNum));
    }
  }

  return (
    <label
      className={cn(
        'relative flex cursor-pointer flex-col items-center gap-1 rounded-lg border p-2 pb-2.5 transition-all',
        isSelected
          ? 'border-brand ring-2 ring-brand/20'
          : 'border-border-subtle hover:border-brand/40',
      )}
    >
      <input
        type="radio"
        aria-label={label}
        checked={isSelected}
        onChange={onClick}
        className="sr-only"
      />
      {isSelected && (
        <span className="absolute top-1 right-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-brand text-[7px] font-bold text-brand-fg">
          ✓
        </span>
      )}
      <div className="flex h-7 items-center justify-center">
        <div
          style={{ width: rw, height: rh }}
          className={cn('rounded-[2px]', isSelected ? 'bg-brand' : 'bg-gray-200 dark:bg-gray-600')}
        />
      </div>
      <span
        className={cn(
          'text-[10px] font-medium leading-none',
          isSelected ? 'text-brand' : 'text-fg-3',
        )}
      >
        {label}
      </span>
    </label>
  );
}

function OutputSettingsPopover({
  open,
  onClose,
  batchSize,
  onBatchSize,
  sizeTier,
  onSizeTier,
  aspectRatio,
  onAspectRatio,
  task,
  totalCost,
}: OutputSettingsProps) {
  const isMatchLook = task === 'match-style';
  const visibleRatios = isMatchLook ? MATCH_LOOK_ASPECT_RATIOS : VISIBLE_ASPECT_RATIOS;
  const dims = calculateOutputDimensions(aspectRatio, sizeTier);

  if (!open) return null;
  return (
    <dialog
      open
      className="absolute bottom-full left-0 z-50 mb-2 w-72 rounded-xl border border-border-subtle bg-white p-4 shadow-xl dark:bg-gray-900"
      aria-label="Output settings"
    >
      {/* Images Generated — brand-blue range slider + credits pill */}
      <div className="mb-4">
        <div className="mb-1 flex items-center justify-between">
          <label htmlFor="batch-size" className="text-xs font-medium text-fg-2">
            Images generated
          </label>
          <span className="text-xs font-semibold text-fg">{batchSize}</span>
        </div>
        <input
          id="batch-size"
          aria-label="number of images"
          type="range"
          min={BATCH_SIZE_MIN}
          max={BATCH_SIZE_MAX}
          step={1}
          value={batchSize}
          onChange={(e) => onBatchSize(Number(e.target.value))}
          className="w-full cursor-pointer appearance-none rounded-full"
          style={{ accentColor: 'hsl(var(--accent))' }}
        />
        <div className="mt-1.5 flex justify-center">
          <span className="rounded-full bg-brand/10 px-2.5 py-0.5 text-[10px] font-semibold text-brand">
            Costs {totalCost} Image Credit{totalCost !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Set output size — brand-blue active border, resolution display */}
      <div className="mb-4">
        <p className="mb-1.5 text-xs font-medium text-fg-2">Set output size</p>
        <div className="flex gap-1.5" role="radiogroup" aria-label="Output size">
          {OUTPUT_SIZE_TIERS.map((t) => (
            <label
              key={t}
              className={cn(
                'flex-1 cursor-pointer rounded-md border py-1 text-center text-xs font-medium capitalize transition-colors',
                sizeTier === t
                  ? 'border-brand text-brand'
                  : 'border-border-subtle text-fg-2 hover:border-brand/40',
              )}
            >
              <input
                type="radio"
                checked={sizeTier === t}
                onChange={() => onSizeTier(t)}
                className="sr-only"
              />
              {t === 'xl' ? 'XL' : t.charAt(0).toUpperCase() + t.slice(1)}
            </label>
          ))}
        </div>
        <p className="mt-1 text-[11px] text-fg-3">
          {dims ? `Resolution: ${dims.w}×${dims.h} px` : 'Matches source'}
        </p>
      </div>

      {/* Set output dimensions — proportional rectangle tiles, 3 cols */}
      <div className="mb-4">
        <p className="mb-1.5 text-xs font-medium text-fg-2">Set output dimensions</p>
        <div className="grid grid-cols-3 gap-1.5" role="radiogroup" aria-label="Output dimensions">
          {visibleRatios.map((r) => (
            <AspectRatioTile
              key={r}
              ratio={r}
              label={ASPECT_RATIO_LABELS[r]}
              isSelected={aspectRatio === r}
              onClick={() => onAspectRatio(r)}
            />
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg bg-brand px-4 py-1.5 text-sm font-medium text-brand-fg hover:bg-brand-hover"
        >
          Done
        </button>
      </div>
    </dialog>
  );
}

// ─── EnhanceSettingsPopover ────────────────────────────────────────────────────────────────────────────────────────

const PHOTO_STYLE_LABELS: Record<EnhancePhotoStyle, string> = {
  auto: 'Auto',
  professional: 'Professional',
  creative: 'Creative',
  natural: 'Natural',
  bold: 'Bold',
  elegant: 'Elegant',
  playful: 'Playful',
};

type EnhanceSettingsPopoverProps = {
  open: boolean;
  onClose: () => void;
  photographer: EnhancePhotographer;
  onPhotographer: (p: EnhancePhotographer) => void;
  photoStyle: EnhancePhotoStyle;
  onPhotoStyle: (s: EnhancePhotoStyle) => void;
};

function EnhanceSettingsPopover({
  open,
  onClose,
  photographer,
  onPhotographer,
  photoStyle,
  onPhotoStyle,
}: EnhanceSettingsPopoverProps) {
  if (!open) return null;
  return (
    <dialog
      open
      className="absolute bottom-full left-0 z-50 mb-2 w-72 rounded-xl border border-border-subtle bg-white p-4 shadow-xl dark:bg-gray-900"
      aria-label="Enhance settings"
    >
      {/* Your Photographer — Express (faster) vs Art Director (higher fidelity) */}
      <div className="mb-4">
        <p className="mb-1.5 text-xs font-medium text-fg-2">Your Photographer</p>
        <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Photographer">
          {(
            [
              ['express', 'Express', '20–35s'],
              ['art-director', 'Art Director', '25–40s'],
            ] as const
          ).map(([value, label, duration]) => (
            <label
              key={value}
              className={cn(
                'flex cursor-pointer flex-col items-start rounded-lg border p-2.5 text-left transition-colors',
                photographer === value
                  ? 'border-brand bg-brand/5'
                  : 'border-border-subtle hover:border-brand/40',
              )}
            >
              <input
                type="radio"
                checked={photographer === value}
                onChange={() => onPhotographer(value)}
                className="sr-only"
              />
              <span
                className={cn(
                  'text-xs font-medium',
                  photographer === value ? 'text-brand' : 'text-fg',
                )}
              >
                {label}
              </span>
              <span className="mt-0.5 text-[10px] text-fg-3">{duration}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Photo Style — 7 chip options */}
      <div className="mb-4">
        <p className="mb-1.5 text-xs font-medium text-fg-2">Photo Style</p>
        <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Photo style">
          {(Object.keys(PHOTO_STYLE_LABELS) as EnhancePhotoStyle[]).map((style) => (
            <label
              key={style}
              className={cn(
                'cursor-pointer rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                photoStyle === style
                  ? 'border-brand bg-brand/10 text-brand'
                  : 'border-border-subtle text-fg-2 hover:border-brand/40',
              )}
            >
              <input
                type="radio"
                checked={photoStyle === style}
                onChange={() => onPhotoStyle(style)}
                className="sr-only"
              />
              {PHOTO_STYLE_LABELS[style]}
            </label>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg bg-brand px-4 py-1.5 text-sm font-medium text-brand-fg hover:bg-brand-hover"
        >
          Done
        </button>
      </div>
    </dialog>
  );
}

// ─── PromptHistoryPopover ──────────────────────────────────────────────────────
//
// Vertical list of the user's last N submitted prompts (iter 37). Anchored
// on the clock-icon button next to the mode pill so the cluster reads
// "current mode + recall past prompts". Empty state mirrors the legacy
// copy.

type PromptHistoryPopoverProps = {
  open: boolean;
  entries: ReadonlyArray<{ prompt: string; createdAt: number }>;
  onPick: (prompt: string) => void;
  onClose: () => void;
};

/** Truncate a prompt for the list row at 80 chars per the brief. */
/**
 * Maps the wire `aiProvider` value onto a short user-facing chip label
 * (iter 100). Returns `null` for providers we don't surface (e.g. the
 * placeholder used pre-AI-wiring, or unknown values from old drafts).
 * Keep this in sync with `apps/backend/src/providers/image/*.ts`.
 */
function providerLabel(aiProvider: string | null | undefined): string | null {
  switch (aiProvider) {
    case 'bfl':
      return 'BFL';
    case 'stub':
      return 'Stub';
    case 'placeholder':
      return null;
    default:
      return null;
  }
}

function truncatePrompt(prompt: string, max = 80): string {
  if (prompt.length <= max) return prompt;
  return `${prompt.slice(0, max).trimEnd()}…`;
}

/**
 * Trigger a file download for `url` (iter 88). Uses fetch → blob → anchor
 * click instead of `window.open` so users actually get a file save dialog
 * rather than a new tab. Falls back to opening the URL on fetch failure
 * (CORS-locked origins, network errors). Inferred extension from the URL
 * path; defaults to `.png`.
 */
async function downloadDraft(url: string, baseName = 'ai-product-studio-draft'): Promise<void> {
  try {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) throw new Error(`download fetch failed: ${res.status}`);
    const blob = await res.blob();
    const ext = (url.match(/\.(png|jpe?g|webp|gif)(?:\?|$)/i)?.[1] ?? 'png').toLowerCase();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = `${baseName}.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
  } catch {
    // Fallback for cross-origin URLs that block fetch — opens in a new tab
    // so the user can save manually via the browser's image context menu.
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

function PromptHistoryPopover({ open, entries, onPick, onClose }: PromptHistoryPopoverProps) {
  if (!open) return null;
  return (
    <>
      {/* Click-outside backdrop. Transparent so the popover still reads as
       * anchored on its button, but click-anywhere-else dismisses cleanly
       * (mirrors Inspiration/Compare modal chrome). Hidden from assistive
       * tech — the X button + Esc key carry the explicit close affordance. */}
      <button
        type="button"
        aria-hidden="true"
        onClick={onClose}
        className="fixed inset-0 z-40 cursor-default bg-transparent"
        tabIndex={-1}
      />
      <div
        className="absolute bottom-full left-0 z-50 mb-2 w-80 rounded-xl border border-border-subtle bg-white shadow-xl dark:bg-gray-900"
        aria-label="Prompt history"
      >
        <div className="flex items-center justify-between border-b border-border-subtle px-4 py-2.5">
          <h2 className="text-sm font-semibold">Recent prompts</h2>
          <button
            type="button"
            aria-label="Close prompt history"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>
        {entries.length === 0 ? (
          <p className="px-4 py-4 text-xs text-fg-3">
            No history yet — your last 20 prompts will land here.
          </p>
        ) : (
          <ul className="max-h-72 overflow-y-auto py-1">
            {entries.map((entry, idx) => {
              // Composite key keeps adjacent identical prompts (different
              // timestamps after dedupe-refresh) stable on re-render.
              const key = `${entry.createdAt}:${idx}`;
              return (
                <li key={key}>
                  <button
                    type="button"
                    onClick={() => onPick(entry.prompt)}
                    className="flex w-full flex-col items-start gap-0.5 px-4 py-2 text-left transition-colors hover:bg-brand/5"
                  >
                    <span className="line-clamp-2 text-xs text-fg">
                      {truncatePrompt(entry.prompt)}
                    </span>
                    <span className="text-[10px] text-fg-3">
                      {formatRelativeTime(entry.createdAt)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────────────────────────────────

export function PhotoStudioPage() {
  const { productUid: rawUid } = useParams<{ productUid: string }>();
  const productUid = rawUid ? decodeURIComponent(rawUid) : '';
  const navigate = useNavigate();

  // Fire `product.studio_opened` exactly once per productUid change. We
  // emit even if the product isn't found yet (productsQuery still
  // loading) — the event is about user intent to open the studio for a
  // specific product, not whether the product resolves successfully.
  useEffect(() => {
    if (!productUid) return;
    track('product.studio_opened', { productUid });
  }, [productUid]);

  // Reset the canvas override when the product changes so a stale draft
  // from a previous product doesn't leak onto a new product's canvas.
  // (Match Style + Merge state already reset elsewhere on task/product change.)
  // We read `productUid` inside the body to satisfy biome's useExhaustive-
  // Dependencies — the effect's job is to react to product changes, but
  // it also avoids running with an empty uid pre-route-resolution.
  useEffect(() => {
    if (productUid) setActiveCanvasImage(null);
  }, [productUid]);

  // Aspect-ratio memory effects live further down — after the
  // useState that declares `aspectRatio` (see iter 82 block below).

  // ── Remote data ────────────────────────────────────────────────────────────────────────────
  const meQuery = useMe();
  const productsQuery = useProducts();
  const generationsQuery = useGenerations();
  const generateMutation = useGenerate();
  const submitFeedback = useSubmitFeedback();
  const creditBalanceQuery = useCreditBalance();
  // Pre-iter-85 this was a hardcoded 16. Now derived from the workspace
  // balance endpoint with a graceful fallback for the loading + error
  // paths (so the credits chip + Fit-to-budget math don't crash on a
  // brief network hiccup).
  const availableCredits = creditBalanceQuery.data?.balance ?? CREDIT_BALANCE_FALLBACK;

  // ── Local UI state ──────────────────────────────────────────────────────────────────────────────
  const [task, setTask] = useState<GenerateTask>('enhance');
  const [prompt, setPrompt] = useState('');
  const [batchSize, setBatchSize] = useState(4);
  const [sizeTier, setSizeTier] = useState<OutputSizeTier>('standard');
  // Aspect ratio init: prefer the remembered choice for this product
  // (iter 82). Lazy initializer so we read localStorage only on first
  // mount; subsequent product switches re-read in a useEffect below.
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>(
    () => readAspectRatio(productUid) ?? 'original',
  );

  // Load this product's remembered aspect ratio on product switch (iter 82).
  // Match Look + Merge reset to 'template' / 'original' elsewhere, so this
  // only kicks in for Enhance/Edit where there's no auto-reset rule.
  useEffect(() => {
    if (!productUid) return;
    const remembered = readAspectRatio(productUid);
    if (remembered) setAspectRatio(remembered);
  }, [productUid]);

  // Persist aspect-ratio changes back to localStorage (iter 82). 'template'
  // is excluded — it's a Match-Look transient, not a per-product choice.
  useEffect(() => {
    if (!productUid) return;
    if (aspectRatio === 'template') return;
    writeAspectRatio(productUid, aspectRatio);
  }, [productUid, aspectRatio]);
  /** Aspect ratio saved before entering Match Look mode; restored on exit. */
  const [savedAspectRatio, setSavedAspectRatio] = useState<AspectRatio>('original');
  /** Natural dimensions of the canvas source image — used for mismatch detection. */
  const [srcImgDims, setSrcImgDims] = useState<{ w: number; h: number } | null>(null);

  // Enhance mode settings (photographer + photo style)
  const [photographer, setPhotographer] = useState<EnhancePhotographer>('express');
  const [photoStyle, setPhotoStyle] = useState<EnhancePhotoStyle>('auto');

  const [showModePopover, setShowModePopover] = useState(false);
  const [showOutputSettings, setShowOutputSettings] = useState(false);
  const [showEnhanceSettings, setShowEnhanceSettings] = useState(false);
  const [showInspirationModal, setShowInspirationModal] = useState(false);
  /** Prompt history popover anchored on the clock-icon button (iter 37). */
  const [showPromptHistory, setShowPromptHistory] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  /**
   * Fullscreen image viewer (iter 77). `null` = closed; otherwise the
   * URL to show. Driven by the Zoom action on draft thumbnails + canvas.
   */
  const [zoomedImageUrl, setZoomedImageUrl] = useState<string | null>(null);

  /**
   * Compare modal (iter 80) — side-by-side INITIAL vs GENERATED with a
   * draggable vertical divider. `null` = closed; otherwise the draft URL
   * to compare against the product's live primary image. Split is the
   * percentage (0-100) of the modal's width that shows the GENERATED
   * (right) side; the LEFT shows INITIAL, masked by the split.
   */
  const [compareDraftUrl, setCompareDraftUrl] = useState<string | null>(null);
  const [compareSplit, setCompareSplit] = useState(50);

  /**
   * Sidebar upload (iter 78). File picker → blob URL → set as canvas image
   * for Enhance/Edit. Uploads aren't persisted to GCS yet so this is a
   * visual-only swap; once an image-upload route lands, this hook is the
   * one place to wire it.
   */
  const sidebarUploadRef = useRef<HTMLInputElement>(null);
  const [sidebarUploadError, setSidebarUploadError] = useState<string | null>(null);
  // Drop-target inline error (iter 86) — surfaces validation failures
  // from canvas drops where the sidebar message would be off-screen.
  // Auto-dismisses after 3s via the effect below.
  const [canvasDropError, setCanvasDropError] = useState<string | null>(null);
  useEffect(() => {
    if (!canvasDropError) return;
    const t = setTimeout(() => setCanvasDropError(null), 3000);
    return () => clearTimeout(t);
  }, [canvasDropError]);

  // Match Look state
  const [matchLookTemplate, setMatchLookTemplate] = useState<string | null>(null);

  /**
   * `activeCanvasImage` overrides the default Enhance/Edit canvas image
   * (which is the product's primary "live" image). When a generation
   * completes successfully, the new output URL becomes the canvas image
   * automatically — so the user sees their result on the main surface,
   * not just as a draft thumbnail. Clicking a draft thumbnail in the
   * left sidebar also sets this state; clicking the live image resets it
   * to `null`. Resets on product change.
   *
   * Match Style and Merge tasks have their own canvas state machines
   * and ignore this override.
   */
  const [activeCanvasImage, setActiveCanvasImage] = useState<string | null>(null);
  const [matchLookProducts, setMatchLookProducts] = useState<string[]>([]);
  const [matchLookSubMode, setMatchLookSubMode] = useState<MatchLookSubMode>('scene');
  const [subModeFallbackHint, setSubModeFallbackHint] = useState(false);
  const [perImageInstructions, setPerImageInstructions] = useState<Record<string, string>>({});
  const [openInstructionFor, setOpenInstructionFor] = useState<string | null>(null);
  const [draftInstruction, setDraftInstruction] = useState('');

  // Merge mode state
  const [mergeSubMode, setMergeSubMode] = useState<MergeSubMode>('create-scene');
  const [mergeImages, setMergeImages] = useState<string[]>([]);
  const [mergeSceneImageUrl, setMergeSceneImageUrl] = useState<string | null>(null);
  /**
   * Tracks whether the first Merge generation has fired. The canvas overlay
   * collapse animation (1.2 s, per spec) only plays on that first fire.
   */
  const hasMergeGeneratedRef = useRef(false);
  const [mergeCollapseAnimate, setMergeCollapseAnimate] = useState(false);

  /**
   * Blob URL revocation (iter 90). Every `URL.createObjectURL(file)` call in
   * this component stores its result in one of three state slots:
   * `activeCanvasImage`, `matchLookTemplate`, or `mergeImages[]`. Without
   * explicit `URL.revokeObjectURL` the browser holds the backing File data
   * for the lifetime of the document — a real memory issue for sessions
   * that drop/upload many images. The effects below revoke a blob URL the
   * moment it's no longer referenced by its state slot. HTTP/HTTPS URLs
   * (generation outputs from BFL or placehold.co) are skipped — only
   * `blob:` URLs need revocation.
   */
  useEffect(() => {
    if (!activeCanvasImage?.startsWith('blob:')) return;
    const url = activeCanvasImage;
    return () => URL.revokeObjectURL(url);
  }, [activeCanvasImage]);
  useEffect(() => {
    if (!matchLookTemplate?.startsWith('blob:')) return;
    const url = matchLookTemplate;
    return () => URL.revokeObjectURL(url);
  }, [matchLookTemplate]);
  const prevMergeImagesRef = useRef<string[]>([]);
  useEffect(() => {
    const prev = prevMergeImagesRef.current;
    const next = mergeImages;
    for (const url of prev) {
      if (url.startsWith('blob:') && !next.includes(url)) URL.revokeObjectURL(url);
    }
    prevMergeImagesRef.current = next;
  }, [mergeImages]);

  const promptRef = useRef<HTMLTextAreaElement>(null);
  /** Hidden file input for template upload in Match Look COPY_EMPTY state. */
  const templateUploadRef = useRef<HTMLInputElement>(null);

  /** Briefly true after "Browse images" is clicked — adds a ring to the library panel. */
  const [highlightLibrary, setHighlightLibrary] = useState(false);
  /** Validation error from template file upload (size > 10 MB). */
  const [templateUploadError, setTemplateUploadError] = useState<string | null>(null);

  // ── Prompt history (iter 37) ─────────────────────────────────────────────
  // Scoped per workspace per the legacy pattern. Captures at submit-success
  // (in `handleSubmit`) and renders via the clock-icon popover anchored on
  // the prompt panel's action row.
  const workspaceId = meQuery.data?.workspaceId ?? '';
  const { history: promptHistory, push: pushPromptToHistory } = usePromptHistory(workspaceId);

  // Last-task memory (iter 83). Workspace-scoped. Load on workspace
  // resolve, persist on task change. Guarded so the initial-render
  // default 'enhance' doesn't get written before we've had a chance to
  // read the remembered value.
  const lastTaskHydratedRef = useRef(false);
  useEffect(() => {
    if (!workspaceId || lastTaskHydratedRef.current) return;
    const remembered = readLastTask(workspaceId);
    if (remembered) setTask(remembered);
    lastTaskHydratedRef.current = true;
  }, [workspaceId]);
  useEffect(() => {
    if (!workspaceId || !lastTaskHydratedRef.current) return;
    writeLastTask(workspaceId, task);
  }, [workspaceId, task]);

  // ── Prompt-input state machine ─────────────────────────────────────────────────────────────────────────────
  const isGenerating = generateMutation.isPending;

  // `showGeneratingState` lags `isGenerating` on the trailing edge by
  // GENERATING_STATE_DELAY_MS so the rotating tips stay on screen during the
  // success transition rather than snapping away mid-animation.
  const [showGeneratingState, setShowGeneratingState] = useState(false);
  useEffect(() => {
    if (isGenerating) {
      setShowGeneratingState(true);
      return;
    }
    const id = setTimeout(() => setShowGeneratingState(false), GENERATING_STATE_DELAY_MS);
    return () => clearTimeout(id);
  }, [isGenerating]);

  // Detect pending → success transition to show the post-gen feedback banner
  // ("✓ Did you know? Remember to review your images — AI can make mistakes.").
  const mutationStatus = generateMutation.status;
  const prevMutationStatusRef = useRef(mutationStatus);
  const [feedbackVisible, setFeedbackVisible] = useState(false);
  const [feedbackDismissed, setFeedbackDismissed] = useState(false);
  // ResultsRibbon slide-in (iter 79). Visible for 5s after a successful
  // generation, then auto-dismisses. Driven by the same pending→success
  // edge as the feedback banner.
  const [resultsRibbonVisible, setResultsRibbonVisible] = useState(false);
  const [resultsBatchSize, setResultsBatchSize] = useState(1);
  useEffect(() => {
    const prev = prevMutationStatusRef.current;
    prevMutationStatusRef.current = mutationStatus;
    if (prev !== 'pending' || mutationStatus !== 'success') return;
    setFeedbackDismissed(false);
    setFeedbackVisible(true);
    setResultsRibbonVisible(true);
    setResultsBatchSize(batchSize);
    const feedbackTimer = setTimeout(() => setFeedbackVisible(false), FEEDBACK_BANNER_DURATION_MS);
    const ribbonTimer = setTimeout(() => setResultsRibbonVisible(false), 5000);
    return () => {
      clearTimeout(feedbackTimer);
      clearTimeout(ribbonTimer);
    };
  }, [mutationStatus, batchSize]);

  const shouldShowFeedback = feedbackVisible && !feedbackDismissed;

  function handleFeedback(rating: FeedbackRating) {
    setFeedbackDismissed(true);
    const generationId = generateMutation.data?.id;
    if (!generationId) return;
    submitFeedback.mutate(
      { generationId, rating },
      {
        onError: () => {
          setFeedbackError('Feedback not saved — try again');
          setTimeout(() => setFeedbackError(null), 4_000);
        },
      },
    );
  }

  // Rotating tips while the generating state is active (iter 39). Pool +
  // cadence (8 s / 300 ms) live in `src/lib/processing-tips.ts`; the hook
  // resets when the active task changes so a mode switch mid-generation
  // doesn't desync the pool.
  const { tip: rotatingTip, visible: tipVisible } = useRotatingTip(task, showGeneratingState);

  // Content state for the prompt panel (spec §"Prompt-input state machine").
  const contentState: 'generating' | 'feedback' | 'editing' = showGeneratingState
    ? 'generating'
    : shouldShowFeedback
      ? 'feedback'
      : 'editing';

  // ── Derived data ─────────────────────────────────────────────────────────────────────────────
  const products: UniversalProduct[] = productsQuery.data?.items ?? [];
  const product = products.find((p) => p.uid === productUid);

  const liveImages: string[] = product ? [getWixMediaSafeUrl(product.primaryImageUrl)] : [];
  const generations: GenerationJob[] = (generationsQuery.data?.items ?? []).filter(
    (g) => g.productUid === productUid,
  );
  const draftImages: string[] = generations
    .filter((g) => g.status === 'COMPLETED' && g.outputImageUrl)
    .map((g) => g.outputImageUrl as string);

  /** Set of output URLs produced by AI (used for Match Style eligibility). */
  const aiGeneratedUrls = useMemo(() => new Set(draftImages), [draftImages]);

  /**
   * Map<draftUrl, aiProvider> for the DraftCard provider badge (iter 100).
   * Lets the UI show "BFL" / "Stub" so users + dev see at a glance which
   * provider generated each draft. Built from the generations array so a
   * reload still labels historical drafts correctly.
   */
  const providerByDraft = useMemo(() => {
    const map = new Map<string, string>();
    for (const g of generations) {
      if (g.outputImageUrl && g.aiProvider) map.set(g.outputImageUrl, g.aiProvider);
    }
    return map;
  }, [generations]);

  const isTemplateAiGenerated = matchLookTemplate ? aiGeneratedUrls.has(matchLookTemplate) : false;

  // ── Credit math ─────────────────────────────────────────────────────────────────────────────
  const costInputs: CostInputs = {
    task,
    sizeTier,
    batchSize,
    matchLookCount: task === 'match-style' ? Math.max(matchLookProducts.length, 1) : undefined,
    matchLookSubMode: task === 'match-style' ? matchLookSubMode : undefined,
    mergeImageCount: task === 'merge' ? Math.max(mergeImages.length, MERGE_MIN_IMAGES) : undefined,
  };
  const totalCost = getTotalCost(costInputs);
  const hasMismatch = detectAspectMismatch(aspectRatio, srcImgDims?.w, srcImgDims?.h) !== null;
  const creditsSkin = getCreditsSkin({
    totalCredits: totalCost,
    availableCredits: availableCredits,
    hasMismatch,
    sizeTier,
  });
  const fittedBatch = fitToBudget(costInputs, availableCredits);
  // How many images are affordable at the current tier (for the over-budget message).
  const maxAffordableAtTier = Math.floor(availableCredits / getPerUnitCost(costInputs));
  // Verbatim spec strings: "Can generate X at this size" / "Not enough credits at this size".
  const overBudgetMsg =
    maxAffordableAtTier > 0
      ? `Can generate ${maxAffordableAtTier} at this size`
      : 'Not enough credits at this size';

  // ── Match Look canvas state machine ─────────────────────────────────────────────────────────────────
  type MatchLookState = 'COPY_EMPTY' | 'COPY_EDIT_FEW' | 'COPY_CONFIRM';
  const matchLookState: MatchLookState = useMemo(() => {
    if (!matchLookTemplate) return 'COPY_EMPTY';
    if (matchLookProducts.length === 0) return 'COPY_EDIT_FEW';
    return 'COPY_CONFIRM';
  }, [matchLookTemplate, matchLookProducts]);

  // Preload the primary product image before the canvas <img> renders so the
  // browser fetches it early. Imperative DOM mutation because Vite bundles
  // the <head> statically and we need a dynamic URL from the CDN.
  useEffect(() => {
    const url = getWixMediaSafeUrl(product?.primaryImageUrl);
    if (!url) return;
    const link = document.createElement('link');
    link.rel = 'preload';
    link.setAttribute('as', 'image');
    link.href = url;
    document.head.appendChild(link);
    return () => {
      if (document.head.contains(link)) document.head.removeChild(link);
    };
  }, [product?.primaryImageUrl]);

  // ── Escape key handler ──────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setShowInspirationModal(false);
        setShowModePopover(false);
        setShowOutputSettings(false);
        setShowEnhanceSettings(false);
        setShowPromptHistory(false);
        setZoomedImageUrl(null);
        setCompareDraftUrl(null);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Clipboard paste listener — active only in Match Look COPY_EMPTY state.
  // Mirrors the legacy `useClipboardPaste` hook (spec: match-look.md §State 1).
  useEffect(() => {
    if (task !== 'match-style' || matchLookTemplate !== null) return;
    function onPaste(e: ClipboardEvent) {
      const file = e.clipboardData?.files[0];
      if (!file || !file.type.startsWith('image/')) return;
      setTemplateUploadError(null);
      const url = URL.createObjectURL(file);
      // Pasted images are never AI-generated → auto-fallback from Match Style.
      if (matchLookSubMode === 'style') {
        setMatchLookSubMode('scene');
        setSubModeFallbackHint(true);
      }
      setMatchLookTemplate(url);
      setMatchLookProducts([]);
    }
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [task, matchLookTemplate, matchLookSubMode]);

  // ── Handlers ───────────────────────────────────────────────────────────────────────────────────────────

  /**
   * Change the active generation task.
   *
   * iter 36: when entering Match Look, save the current aspect ratio and
   * switch to 'template'; when leaving, restore the saved ratio.
   */
  function handleTaskChange(next: GenerateTask) {
    const enteringMatchLook = next === 'match-style';
    const leavingMatchLook = task === 'match-style' && next !== 'match-style';
    const leavingMerge = task === 'merge' && next !== 'merge';

    if (enteringMatchLook) {
      setSavedAspectRatio(aspectRatio);
      setAspectRatio('template');
    } else if (leavingMatchLook) {
      setAspectRatio(savedAspectRatio);
    }

    setTask(next);
    setShowModePopover(false);

    if (leavingMatchLook) {
      setMatchLookTemplate(null);
      setMatchLookProducts([]);
      setMatchLookSubMode('scene');
      setSubModeFallbackHint(false);
      setPerImageInstructions({});
    }
    if (leavingMerge) {
      setMergeImages([]);
      setMergeSubMode('create-scene');
      setMergeSceneImageUrl(null);
      setMergeCollapseAnimate(false);
      hasMergeGeneratedRef.current = false;
    }
  }

  function handleMergeImageToggle(url: string) {
    setMergeImages((prev) => {
      if (prev.includes(url)) {
        const next = prev.filter((u) => u !== url);
        if (mergeSubMode === 'add-to-scene' && mergeSceneImageUrl === url) {
          setMergeSceneImageUrl(next[0] ?? null);
        }
        return next;
      }
      if (prev.length >= MAX_MERGE_IMAGES) return prev;
      return [...prev, url];
    });
  }

  function handleSetAsSceneImage(url: string) {
    setMergeSceneImageUrl(url);
  }

  function handleMergeSubModeChange(mode: MergeSubMode) {
    setMergeSubMode(mode);
    if (mode === 'add-to-scene' && !mergeSceneImageUrl && mergeImages.length > 0) {
      setMergeSceneImageUrl(mergeImages[0] ?? null);
    }
  }

  function handleTemplateSelect(url: string) {
    const isAiGenerated = aiGeneratedUrls.has(url);
    // If we were in Match Style and the new template isn't AI-generated → auto-fallback.
    if (matchLookSubMode === 'style' && !isAiGenerated) {
      setMatchLookSubMode('scene');
      setSubModeFallbackHint(true);
    } else {
      setSubModeFallbackHint(false);
    }
    setMatchLookTemplate(url);
    setMatchLookProducts([]);
  }

  function handleTemplateFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setTemplateUploadError('Image must be under 10 MB.');
      e.target.value = '';
      return;
    }
    setTemplateUploadError(null);
    const url = URL.createObjectURL(file);
    handleTemplateSelect(url);
    e.target.value = '';
  }

  /**
   * Sidebar `+ Upload image` handler (iter 78). Validates 10 MB cap +
   * image MIME, sets as the Enhance/Edit canvas image. Match Style mode
   * gets routed to the template upload path instead (which is what the
   * user almost certainly meant).
   */
  function handleSidebarFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const err = ingestImageFile(file);
    setSidebarUploadError(err);
  }

  /**
   * Shared file-ingestion path used by the sidebar upload button (iter 78)
   * and drag-drop zones (iter 81). Validates → routes by task. Returns
   * true on success.
   */
  /**
   * Validates a file + routes it into the task-appropriate slot. Returns
   * the rejection reason (or null on success) so callers can surface the
   * error in their own UI region (sidebar message vs. canvas toast).
   */
  function ingestImageFile(file: File): string | null {
    if (!file.type.startsWith('image/')) return 'Only image files are supported.';
    if (file.size > 10 * 1024 * 1024) return 'Image must be under 10 MB.';
    const url = URL.createObjectURL(file);
    if (task === 'match-style') {
      // COPY_EMPTY: dropped file becomes the template.
      // COPY_EDIT_FEW / COPY_CONFIRM: dropped file adds to the product selection.
      if (matchLookState === 'COPY_EMPTY') {
        handleTemplateSelect(url);
      } else {
        handleMatchLookProductToggle(url);
      }
      return null;
    }
    if (task === 'merge') {
      if (mergeImages.length >= MAX_MERGE_IMAGES) {
        return `Max ${MAX_MERGE_IMAGES} images for Merge — remove one first.`;
      }
      handleMergeImageToggle(url);
      return null;
    }
    // Default (Enhance/Edit): show on canvas immediately.
    setActiveCanvasImage(url);
    return null;
  }

  /**
   * Drag-and-drop handler (iter 81). Accepts the first dropped file that
   * passes `ingestImageFile`. Lights up the drop target via the
   * `isDragOver` flag.
   */
  const [isDragOver, setIsDragOver] = useState(false);
  function handleDragOver(e: React.DragEvent) {
    if (!e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();
    setIsDragOver(true);
  }
  function handleDragLeave(e: React.DragEvent) {
    // Only clear when leaving the drop zone (not bubbling from a child).
    if (e.currentTarget === e.target) setIsDragOver(false);
  }
  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    const err = ingestImageFile(file);
    setCanvasDropError(err);
  }

  function handleMatchLookProductToggle(url: string) {
    setMatchLookProducts((prev) =>
      prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url],
    );
  }

  function handleSubModeChange(mode: MatchLookSubMode) {
    setMatchLookSubMode(mode);
    setSubModeFallbackHint(false);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setGenerateError(null);
    track('studio.generate_submitted', { task, batchSize, sizeTier });
    const submittedAt = Date.now();
    // Trigger merge overlay collapse animation on first generation.
    if (task === 'merge' && !hasMergeGeneratedRef.current) {
      hasMergeGeneratedRef.current = true;
      setMergeCollapseAnimate(true);
    }
    try {
      const job = await generateMutation.mutateAsync({
        productUid,
        task,
        prompt,
        aspectRatio,
        sizeTier,
        batchSize,
        ...(task === 'enhance' ? { enhance: { photographer, photoStyle } } : {}),
        ...(task === 'match-style' && matchLookTemplate
          ? {
              matchLook: {
                subMode: matchLookSubMode,
                templateImageUrl: matchLookTemplate,
                productImageUrls:
                  matchLookProducts.length > 0
                    ? matchLookProducts
                    : [liveImages[0] ?? matchLookTemplate],
                perImageInstructions:
                  Object.keys(perImageInstructions).length > 0 ? perImageInstructions : undefined,
              },
            }
          : {}),
        ...(task === 'merge' && mergeImages.length >= MERGE_MIN_IMAGES
          ? {
              merge: {
                subMode: mergeSubMode,
                productImageUrls: mergeImages,
                ...(mergeSubMode === 'add-to-scene' && (mergeSceneImageUrl ?? mergeImages[0])
                  ? { sceneImageUrl: mergeSceneImageUrl ?? mergeImages[0] }
                  : {}),
              },
            }
          : {}),
      });
      // Persist the prompt to per-workspace history on success (iter 37).
      // Empty/whitespace prompts are filtered out inside the helper.
      pushPromptToHistory(prompt);
      // Emit per-job correlation event so PostHog funnels can join on
      // jobId when linking frontend sessions to backend generation records.
      track('generation.created', { jobId: job.id, productUid, task });
      // Switch the canvas to the new result so the user sees their
      // generation immediately, not just as a sidebar draft (iter 76).
      // Only applies to Enhance/Edit; Match Style + Merge have their
      // own canvas state machines that ignore this override.
      if ((task === 'enhance' || task === 'edit') && job.outputImageUrl) {
        setActiveCanvasImage(job.outputImageUrl);
      }
      track('studio.generate_completed', {
        task,
        durationMs: Date.now() - submittedAt,
        creditsUsed: job.creditsUsed ?? 0,
        aiProvider: job.aiProvider ?? 'unknown',
      });
    } catch (err) {
      setGenerateError(humanizeError(err));
      track('studio.generate_failed', {
        task,
        durationMs: Date.now() - submittedAt,
        errorCode: err instanceof ApiError ? err.body.code : null,
      });
    }
  }

  // ── Loading / error states ──────────────────────────────────────────────────────────────────────
  if (meQuery.isPending || productsQuery.isPending) {
    return (
      <div className="flex h-screen items-center justify-center">
        <span className="text-gray-500">Loading studio…</span>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4">
        <p className="text-lg font-semibold text-gray-700 dark:text-gray-300">Product not found</p>
        <Link to="/products" className="text-violet-600 underline">
          Back to Products
        </Link>
      </div>
    );
  }

  // ── Computed display values ──────────────────────────────────────────────────────────────────────────
  // Canvas image precedence (Enhance/Edit):
  //   1. activeCanvasImage (latest generation result OR user-clicked draft)
  //   2. product.primaryImageUrl (the live source — default state)
  // Match Style mode shows the selected template instead.
  // Merge has its own canvas rendered elsewhere.
  const isCanvasOverridden = (task === 'enhance' || task === 'edit') && activeCanvasImage !== null;
  const canvasImageUrl =
    task === 'match-style' && matchLookTemplate
      ? matchLookTemplate
      : isCanvasOverridden
        ? (activeCanvasImage as string)
        : getWixMediaSafeUrl(product.primaryImageUrl);
  const canvasBadge: { label: string; variant: 'live' | 'draft' | 'new' } =
    task === 'match-style' && matchLookTemplate
      ? { label: 'template', variant: 'new' }
      : isCanvasOverridden
        ? { label: 'new', variant: 'new' }
        : { label: 'live', variant: 'live' };

  const SKIN_CLASSES: Record<CreditsSkin, string> = {
    urgent: 'bg-red-100 text-red-700',
    danger: 'bg-red-100 text-red-700',
    warning: 'bg-amber-100 text-amber-700',
    warningLight: 'bg-amber-50 text-amber-600',
    standard: 'bg-violet-100 text-violet-700',
    neutralStandard: 'bg-gray-100 text-gray-600',
  };

  // Verbatim placeholders per spec §"AI hint copy (verbatim)".
  const promptPlaceholder =
    task === 'merge'
      ? mergeSubMode === 'add-to-scene'
        ? 'Describe how the products should appear in the scene…'
        : 'Describe the scene for your products…'
      : 'Describe the scene of your background…';

  // ── Render ────────────────────────────────────────────────────────────────────────────────────
  return (
    <div className="flex h-screen flex-col overflow-hidden bg-white dark:bg-gray-950">
      {/* ── Top bar ──────────────────────────────────────────────────────────────────────────────────────── */}
      <header className="flex h-14 shrink-0 items-center justify-between border-b border-gray-100 px-4 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <Logo />
          <Link
            to="https://docs.example.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-gray-500 hover:underline"
          >
            Learn more
          </Link>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'rounded-full px-3 py-1 text-xs font-semibold',
              SKIN_CLASSES[creditsSkin],
            )}
          >
            {availableCredits} credits
          </span>
          <ThemeToggle />
          <button
            type="button"
            aria-label="Close studio"
            onClick={() => navigate('/products')}
            className="rounded-lg p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            ✕
          </button>
        </div>
      </header>

      {/* ── Body ───────────────────────────────────────────────────────────────────────────────────────────── */}
      <div className="flex min-h-0 flex-1">
        {/* ── Left panel ──────────────────────────────────────────────────────────────────────────────────────────── */}
        <aside className="flex w-64 shrink-0 flex-col gap-4 overflow-y-auto border-r border-gray-100 p-4 dark:border-gray-800">
          {/* Product switcher */}
          <div>
            <button
              type="button"
              aria-expanded={false}
              aria-label={product.title}
              className="flex w-full items-center gap-2 rounded-lg border p-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <img
                src={getWixMediaSafeUrl(product.primaryImageUrl)}
                alt={product.title}
                className="h-8 w-8 rounded object-cover"
              />
              <span className="flex-1 truncate font-medium">{product.title}</span>
              <span className="text-gray-400">▾</span>
            </button>
          </div>

          {/* Product Images card — ring animation when "Browse images" is clicked in COPY_EMPTY */}
          <div
            data-testid="product-images-card"
            className={cn(
              'rounded-xl border p-3 transition-all duration-500',
              highlightLibrary
                ? 'border-violet-400 ring-2 ring-violet-300 dark:ring-violet-600'
                : 'border-gray-100 dark:border-gray-800',
            )}
          >
            <h2 className="mb-3 text-sm font-semibold">Product Images</h2>

            {/* Merge mode info strip */}
            {task === 'merge' && (
              <p className="mb-3 rounded-lg bg-blue-50 px-2.5 py-2 text-[11px] text-blue-700 dark:bg-blue-900/20 dark:text-blue-300">
                Select 2-8 images to add to your merge.
              </p>
            )}

            {/* Live Images */}
            <section>
              <p className="mb-2 text-xs font-medium text-gray-500">
                Live Images ({liveImages.length})
              </p>
              <div className="grid grid-cols-3 gap-1.5">
                {liveImages.map((url) => {
                  const isTemplate = task === 'match-style' && matchLookTemplate === url;
                  const isSelectedProduct =
                    task === 'match-style' && matchLookProducts.includes(url);
                  const mergeIdx = task === 'merge' ? mergeImages.indexOf(url) : -1;
                  const isMergeSelected = mergeIdx !== -1;
                  // Enhance/Edit: the live image is the canvas source; clicking
                  // it resets the canvas override (returns from a draft view to
                  // the original).
                  const isLiveCanvas =
                    (task === 'enhance' || task === 'edit') && activeCanvasImage === null;
                  let thumbTitle = '';
                  if (task === 'match-style') {
                    if (matchLookState === 'COPY_EMPTY') thumbTitle = 'Set as template';
                    else if (isTemplate) thumbTitle = '';
                    else if (isSelectedProduct) thumbTitle = 'Remove from selection';
                    else thumbTitle = 'Select product image';
                  } else if (task === 'merge') {
                    thumbTitle = isMergeSelected ? 'Remove from merge' : 'Add to merge';
                  } else if (task === 'enhance' || task === 'edit') {
                    thumbTitle = isLiveCanvas ? '' : 'Back to original';
                  }
                  return (
                    <Thumbnail
                      key={url}
                      url={url}
                      alt="Live image"
                      isSelected={
                        isTemplate || isSelectedProduct || isMergeSelected || isLiveCanvas
                      }
                      title={thumbTitle}
                      onClick={
                        task === 'match-style'
                          ? matchLookState === 'COPY_EMPTY'
                            ? () => handleTemplateSelect(url)
                            : !isTemplate
                              ? () => handleMatchLookProductToggle(url)
                              : undefined
                          : task === 'merge'
                            ? () => handleMergeImageToggle(url)
                            : task === 'enhance' || task === 'edit'
                              ? () => setActiveCanvasImage(null)
                              : undefined
                      }
                      badge={
                        isMergeSelected ? (
                          <span className="absolute top-0.5 left-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-violet-600 text-[9px] font-bold text-white">
                            {mergeIdx + 1}
                          </span>
                        ) : undefined
                      }
                    />
                  );
                })}
              </div>
            </section>

            {/* Draft Images */}
            {draftImages.length > 0 && (
              <section className="mt-3">
                <p className="mb-2 text-xs font-medium text-gray-500">
                  Draft Images ({draftImages.length})
                </p>
                <div className="grid grid-cols-3 gap-1.5">
                  {draftImages.map((url, idx) => {
                    const isTemplate = task === 'match-style' && matchLookTemplate === url;
                    const isSelectedProduct =
                      task === 'match-style' && matchLookProducts.includes(url);
                    const mergeIdx = task === 'merge' ? mergeImages.indexOf(url) : -1;
                    const isMergeSelected = mergeIdx !== -1;
                    const isCanvasDraft =
                      (task === 'enhance' || task === 'edit') && activeCanvasImage === url;
                    let thumbTitle = '';
                    if (task === 'match-style') {
                      if (matchLookState === 'COPY_EMPTY') thumbTitle = 'Set draft as template';
                      else if (isTemplate) thumbTitle = '';
                      else if (isSelectedProduct) thumbTitle = 'Remove from selection';
                      else thumbTitle = 'Select draft for match look';
                    } else if (task === 'merge') {
                      thumbTitle = isMergeSelected ? 'Remove from merge' : 'Add to merge';
                    } else if (task === 'enhance' || task === 'edit') {
                      thumbTitle = isCanvasDraft ? '' : 'Open on canvas';
                    }
                    return (
                      <Thumbnail
                        key={url}
                        url={url}
                        alt={`Draft ${idx + 1}`}
                        isSelected={
                          isTemplate || isSelectedProduct || isMergeSelected || isCanvasDraft
                        }
                        title={thumbTitle}
                        onClick={
                          task === 'match-style'
                            ? matchLookState === 'COPY_EMPTY'
                              ? () => handleTemplateSelect(url)
                              : !isTemplate
                                ? () => handleMatchLookProductToggle(url)
                                : undefined
                            : task === 'merge'
                              ? () => handleMergeImageToggle(url)
                              : task === 'enhance' || task === 'edit'
                                ? () => setActiveCanvasImage(url)
                                : undefined
                        }
                        badge={
                          <>
                            {isMergeSelected && (
                              <span className="absolute top-0.5 left-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-violet-600 text-[9px] font-bold text-white">
                                {mergeIdx + 1}
                              </span>
                            )}
                            {/* iter 100: aiProvider badge — surfaces which AI
                                generated this draft. Hidden for placeholder /
                                unknown providers so it's a no-op on legacy
                                drafts. */}
                            {(() => {
                              const label = providerLabel(providerByDraft.get(url));
                              if (!label) return null;
                              // The visible text "BFL" / "Stub" is enough for
                              // sighted users; `title` provides the long-form
                              // on hover. Intentionally no aria-label — that
                              // bleeds into the parent button's accessible
                              // name in a11y-aware queries and matched
                              // unrelated /generate/i selectors.
                              return (
                                <span
                                  className="absolute right-0.5 bottom-0.5 rounded bg-black/60 px-1 py-px text-[8px] font-semibold tracking-widest text-white uppercase backdrop-blur-sm"
                                  title={`AI provider: ${label}`}
                                >
                                  {label}
                                </span>
                              );
                            })()}
                          </>
                        }
                        hoverActions={
                          // Show hover actions only on drafts in Enhance/Edit modes
                          // (iter 77 + iter 80). Match Style + Merge modes use
                          // clicks for selection so a hover ribbon would compete
                          // with the primary tap target.
                          task === 'enhance' || task === 'edit' ? (
                            <>
                              <HoverAction
                                label="Zoom"
                                icon={<ZoomIn className="h-3.5 w-3.5" aria-hidden />}
                                onClick={() => setZoomedImageUrl(url)}
                              />
                              <HoverAction
                                label="Compare with original"
                                icon={<SplitSquareVertical className="h-3.5 w-3.5" aria-hidden />}
                                onClick={() => {
                                  setCompareSplit(50);
                                  setCompareDraftUrl(url);
                                }}
                              />
                              <HoverAction
                                label="Download draft"
                                icon={<Download className="h-3.5 w-3.5" aria-hidden />}
                                onClick={() => {
                                  downloadDraft(url);
                                }}
                              />
                              <HoverAction
                                label="Publish — pending Wix connection"
                                icon={<Send className="h-3.5 w-3.5" aria-hidden />}
                                disabled
                                onClick={() => {
                                  /* iter 41-44 ADR 0004 */
                                }}
                              />
                            </>
                          ) : undefined
                        }
                      />
                    );
                  })}
                </div>
              </section>
            )}

            {/* Upload CTA */}
            <button
              type="button"
              onClick={() => sidebarUploadRef.current?.click()}
              className="mt-3 w-full rounded-lg border border-dashed border-gray-300 py-2 text-xs text-gray-400 hover:border-violet-400 hover:text-violet-600"
            >
              + Upload image
            </button>
            <input
              ref={sidebarUploadRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleSidebarFileUpload}
            />
            {sidebarUploadError && (
              <p className="mt-1 text-[11px] text-red-500">{sidebarUploadError}</p>
            )}
          </div>
        </aside>

        {/* ── Main area ──────────────────────────────────────────────────────────────────────────────────────────── */}
        <main className="relative flex flex-1 flex-col">
          {/* ResultsRibbon (iter 79) — slides up from the bottom of the
              canvas region when a generation completes. Auto-dismisses
              after 5s. Two actions: "Back to original" (Enhance/Edit only)
              + × dismiss. Stays above the prompt panel via z-30. */}
          {resultsRibbonVisible && (
            <output
              aria-live="polite"
              className={cn(
                'pointer-events-auto absolute inset-x-0 bottom-0 z-30 mx-auto flex w-fit',
                'translate-y-0 items-center gap-3 rounded-t-xl bg-gray-900 px-4 py-2 text-white shadow-2xl',
                'transition-transform duration-300 ease-out',
                'data-[hidden=true]:translate-y-full',
              )}
            >
              <Sparkles className="h-5 w-5 text-amber-300" aria-hidden />
              <span className="text-sm font-medium">
                {resultsBatchSize} new image{resultsBatchSize !== 1 ? 's' : ''} generated
              </span>
              {(task === 'enhance' || task === 'edit') && activeCanvasImage !== null && (
                <button
                  type="button"
                  onClick={() => {
                    setActiveCanvasImage(null);
                    setResultsRibbonVisible(false);
                  }}
                  className="rounded-md border border-white/30 px-2 py-1 text-xs hover:bg-white/10"
                >
                  Back to original
                </button>
              )}
              <button
                type="button"
                aria-label="Dismiss"
                onClick={() => setResultsRibbonVisible(false)}
                className="flex h-6 w-6 items-center justify-center rounded-md text-white hover:bg-white/10"
              >
                ✕
              </button>
            </output>
          )}
          {/* Canvas — drag-and-drop drop zone (iter 81 + 86). The whole
              canvas region accepts drops; visual feedback via a violet
              inset ring when `isDragOver`. Drops are routed through
              `ingestImageFile` which validates + routes by task:
              Enhance/Edit → canvas image; Match Style → template slot
              (any state, not just empty — iter 86); Merge → mergeImages
              array, with MAX_MERGE_IMAGES guard (iter 86). Failures
              surface as a transient inline toast (canvasDropError). */}
          <div
            data-testid="canvas-drop-zone"
            className={cn(
              'relative flex flex-1 items-center justify-center overflow-hidden bg-gray-50 p-6 dark:bg-gray-900',
              isDragOver && 'ring-2 ring-violet-400 ring-inset',
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {/* Drop-target inline error toast (iter 86). Surfaces drop
                validation failures that would otherwise silently fail on
                the canvas (e.g. Merge at MAX_MERGE_IMAGES). */}
            {canvasDropError && (
              <output
                aria-live="polite"
                aria-atomic="true"
                aria-label="canvas drop error"
                className="absolute top-4 left-1/2 z-30 -translate-x-1/2 rounded-md bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 shadow ring-1 ring-red-200 dark:bg-red-900/40 dark:text-red-200 dark:ring-red-800/60"
              >
                {canvasDropError}
              </output>
            )}
            {task === 'match-style' ? (
              // Match Look canvas variants
              matchLookState === 'COPY_EMPTY' ? (
                // COPY_EMPTY: Choose Your Template (spec §match-look.md "State 1")
                <div className="flex flex-col items-center gap-4 text-center">
                  <h1 className="text-2xl font-bold">Choose Your Template</h1>
                  <p className="max-w-sm text-gray-500">
                    Pick a template image to apply its look across your products.
                  </p>
                  <div className="flex flex-wrap justify-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setHighlightLibrary(true);
                        setTimeout(() => setHighlightLibrary(false), 1500);
                      }}
                      className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700"
                    >
                      Browse images
                    </button>
                    <button
                      type="button"
                      onClick={() => templateUploadRef.current?.click()}
                      className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700"
                    >
                      Upload
                    </button>
                    <button
                      type="button"
                      title="Press Cmd+V (Mac) or Ctrl+V (PC) to paste an image from your clipboard"
                      onClick={async () => {
                        // Async Clipboard API: read image blob if present.
                        // Falls back silently if the user denies permission or
                        // their browser doesn't support it — the global paste
                        // listener (window 'paste' event) still works either way.
                        try {
                          const items = await navigator.clipboard.read();
                          for (const item of items) {
                            const imgType = item.types.find((t) => t.startsWith('image/'));
                            if (!imgType) continue;
                            const blob = await item.getType(imgType);
                            if (blob.size > 10 * 1024 * 1024) {
                              setTemplateUploadError('Image must be under 10 MB.');
                              return;
                            }
                            setTemplateUploadError(null);
                            handleTemplateSelect(URL.createObjectURL(blob));
                            return;
                          }
                          setTemplateUploadError(
                            'No image in clipboard — copy one first, then click Paste.',
                          );
                        } catch {
                          setTemplateUploadError(
                            'Press ⌘V (Mac) or Ctrl+V (PC) with an image in your clipboard.',
                          );
                        }
                      }}
                      className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700"
                    >
                      Paste <span className="text-xs text-gray-400">⌘V</span>
                    </button>
                  </div>
                  {templateUploadError && (
                    <p className="text-sm text-red-500">{templateUploadError}</p>
                  )}
                </div>
              ) : matchLookState === 'COPY_EDIT_FEW' ? (
                // COPY_EDIT_FEW: template picked, no products yet
                <div className="flex flex-col items-start gap-4">
                  <div className="relative">
                    <img
                      src={matchLookTemplate!}
                      alt="Template"
                      className="max-h-72 rounded-xl object-contain shadow"
                    />
                    <button
                      type="button"
                      aria-label="Change template"
                      onClick={() => {
                        setMatchLookTemplate(null);
                        setMatchLookProducts([]);
                      }}
                      className="absolute top-2 right-2 rounded-lg bg-white/80 px-2 py-1 text-xs font-medium shadow hover:bg-white"
                    >
                      Change Template
                    </button>
                  </div>
                  {/* Sub-mode toggle */}
                  <fieldset className="flex gap-4">
                    <legend className="sr-only">Match sub-mode</legend>
                    <label className="flex cursor-pointer items-center gap-1.5 text-sm">
                      <input
                        type="radio"
                        name="matchLookSubMode"
                        value="scene"
                        checked={matchLookSubMode === 'scene'}
                        onChange={() => handleSubModeChange('scene')}
                        aria-label="Match Scene"
                      />
                      Match Scene
                    </label>
                    <label
                      className={cn(
                        'flex cursor-pointer items-center gap-1.5 text-sm',
                        !isTemplateAiGenerated && 'cursor-not-allowed opacity-50',
                      )}
                    >
                      <input
                        type="radio"
                        name="matchLookSubMode"
                        value="style"
                        checked={matchLookSubMode === 'style'}
                        onChange={() => handleSubModeChange('style')}
                        disabled={!isTemplateAiGenerated}
                        aria-label="Match Style"
                      />
                      Match Style
                    </label>
                  </fieldset>
                  {subModeFallbackHint && (
                    <output className="block text-xs text-amber-600">
                      Switched to Match Scene — Match Style requires an AI-generated template.
                    </output>
                  )}
                  <p className="text-sm text-gray-500">Pick the products you want to match</p>
                </div>
              ) : (
                // COPY_CONFIRM
                <div className="flex flex-col items-start gap-4">
                  <div className="relative">
                    <img
                      src={matchLookTemplate!}
                      alt="Template"
                      className="max-h-48 rounded-xl object-contain shadow"
                    />
                    <button
                      type="button"
                      aria-label="Change template"
                      onClick={() => {
                        setMatchLookTemplate(null);
                        setMatchLookProducts([]);
                      }}
                      className="absolute top-2 right-2 rounded-lg bg-white/80 px-2 py-1 text-xs font-medium shadow hover:bg-white"
                    >
                      Change Template
                    </button>
                  </div>
                  <p className="text-sm font-semibold">
                    {matchLookProducts.length} image{matchLookProducts.length !== 1 ? 's' : ''}{' '}
                    ready to match
                  </p>
                  {/* Sub-mode toggle (iter 78) — same control as COPY_EDIT_FEW
                      so users can still flip after picking products. */}
                  <fieldset className="flex gap-4">
                    <legend className="sr-only">Match sub-mode</legend>
                    <label className="flex cursor-pointer items-center gap-1.5 text-sm">
                      <input
                        type="radio"
                        name="matchLookSubModeConfirm"
                        value="scene"
                        checked={matchLookSubMode === 'scene'}
                        onChange={() => handleSubModeChange('scene')}
                      />
                      Match Scene
                    </label>
                    <label
                      className={cn(
                        'flex cursor-pointer items-center gap-1.5 text-sm',
                        !isTemplateAiGenerated && 'cursor-not-allowed opacity-50',
                      )}
                    >
                      <input
                        type="radio"
                        name="matchLookSubModeConfirm"
                        value="style"
                        checked={matchLookSubMode === 'style'}
                        onChange={() => handleSubModeChange('style')}
                        disabled={!isTemplateAiGenerated}
                      />
                      Match Style
                    </label>
                  </fieldset>
                  {/* Product cards */}
                  <div className="flex flex-wrap gap-3">
                    {matchLookProducts.map((url, idx) => {
                      const hasInstruction = !!perImageInstructions[url]?.trim();
                      const instructionText = perImageInstructions[url] ?? '';
                      const isInstructionOpen = openInstructionFor === url;
                      const isMatchScene = matchLookSubMode === 'scene';

                      return (
                        <div key={url} className="relative flex flex-col items-center gap-1">
                          <img
                            src={url}
                            alt={`Product ${idx + 1}`}
                            className="h-24 w-24 rounded-lg object-cover shadow"
                          />
                          <button
                            type="button"
                            aria-label={`Remove product ${idx + 1}`}
                            onClick={() => handleMatchLookProductToggle(url)}
                            className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] text-white"
                          >
                            ×
                          </button>
                          {/* Per-image Add Instructions button — Match Scene only */}
                          {isMatchScene && (
                            <>
                              <button
                                type="button"
                                aria-label={`${
                                  hasInstruction ? 'Edit' : 'Add'
                                } instructions for product ${idx + 1}`}
                                onClick={() => {
                                  setDraftInstruction(instructionText);
                                  setOpenInstructionFor(isInstructionOpen ? null : url);
                                }}
                                className="opacity-70 hover:opacity-100 text-[10px] text-violet-600 underline"
                              >
                                {hasInstruction ? 'Edit instructions' : 'Add Instructions'}
                              </button>
                              {isInstructionOpen && (
                                <dialog
                                  open
                                  aria-label="Per-image instructions"
                                  className="absolute top-full mt-1 z-50 w-56 rounded-xl border border-gray-200 bg-white p-3 shadow-xl dark:border-gray-700 dark:bg-gray-900"
                                >
                                  <textarea
                                    aria-label={`Instructions for product ${idx + 1}`}
                                    value={draftInstruction}
                                    onChange={(e) => setDraftInstruction(e.target.value)}
                                    rows={3}
                                    placeholder="e.g. keep product centered"
                                    className="w-full rounded border border-gray-200 p-2 text-xs dark:border-gray-700 dark:bg-gray-800"
                                  />
                                  {/* Example chips */}
                                  <div className="mt-2 flex flex-wrap gap-1">
                                    {[
                                      'keep product centered',
                                      'copy background only',
                                      'preserve original color',
                                    ].map((chip) => (
                                      <button
                                        key={chip}
                                        type="button"
                                        onClick={() => setDraftInstruction(chip)}
                                        className="rounded-full border border-gray-200 px-2 py-0.5 text-[10px] hover:border-violet-400"
                                      >
                                        {chip}
                                      </button>
                                    ))}
                                  </div>
                                  {/* Actions */}
                                  <div className="mt-2 flex justify-end gap-1.5">
                                    <button
                                      type="button"
                                      aria-label="Cancel"
                                      onClick={() => setOpenInstructionFor(null)}
                                      className="rounded border px-2 py-0.5 text-xs"
                                    >
                                      Cancel
                                    </button>
                                    {hasInstruction && (
                                      <button
                                        type="button"
                                        aria-label="Clear"
                                        onClick={() => {
                                          setPerImageInstructions((prev) => {
                                            const next = { ...prev };
                                            delete next[url];
                                            return next;
                                          });
                                          setOpenInstructionFor(null);
                                        }}
                                        className="rounded border px-2 py-0.5 text-xs text-red-500"
                                      >
                                        Clear
                                      </button>
                                    )}
                                    <button
                                      type="button"
                                      aria-label="Save"
                                      onClick={() => {
                                        if (draftInstruction.trim()) {
                                          setPerImageInstructions((prev) => ({
                                            ...prev,
                                            [url]: draftInstruction.trim(),
                                          }));
                                        } else {
                                          setPerImageInstructions((prev) => {
                                            const next = { ...prev };
                                            delete next[url];
                                            return next;
                                          });
                                        }
                                        setOpenInstructionFor(null);
                                      }}
                                      className="rounded border border-violet-500 px-2 py-0.5 text-xs text-violet-600"
                                    >
                                      Save
                                    </button>
                                  </div>
                                </dialog>
                              )}
                              {hasInstruction && (
                                <span className="max-w-[96px] truncate text-[9px] text-gray-400">
                                  [{idx + 1}] {instructionText}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )
            ) : task === 'merge' ? (
              // Merge canvas
              mergeImages.length === 0 ? (
                // MERGE empty state — orbital placeholder + guide text
                <div
                  aria-label="Merge Images empty state"
                  className={cn(
                    'flex flex-col items-center gap-6 text-center transition-opacity duration-700',
                    mergeCollapseAnimate && 'pointer-events-none opacity-0',
                  )}
                >
                  {/* Orbital visual: dashed ring with three product-slot circles */}
                  <div className="relative flex h-32 w-32 items-center justify-center">
                    <div className="absolute inset-0 rounded-full border-2 border-dashed border-violet-200 dark:border-violet-700" />
                    {[0, 120, 240].map((deg) => (
                      <div
                        key={deg}
                        className="absolute flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-xs text-gray-400 shadow-sm dark:bg-gray-700"
                        style={{
                          transform: `rotate(${deg}deg) translateY(-44px) rotate(-${deg}deg)`,
                        }}
                        aria-hidden="true"
                      >
                        +
                      </div>
                    ))}
                    <div className="z-10 flex h-14 w-14 items-center justify-center rounded-2xl bg-violet-100 text-2xl shadow dark:bg-violet-900/40">
                      🔀
                    </div>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">
                      Merge Images
                    </h2>
                    <p className="mt-1 max-w-xs text-sm text-gray-500">
                      Select images to get started.
                    </p>
                    <p className="mt-2 text-xs text-gray-400">
                      Add 2–8 images from the left panel.
                    </p>
                  </div>
                  {/* Sub-mode toggle — shown even in empty state so tests can find it */}
                  <fieldset className="flex gap-3">
                    <legend className="sr-only">Merge sub-mode</legend>
                    {(
                      [
                        { value: 'create-scene', label: 'Create Scene' },
                        { value: 'add-to-scene', label: 'Add to Scene' },
                      ] as { value: MergeSubMode; label: string }[]
                    ).map(({ value, label }) => (
                      <label
                        key={value}
                        className="flex cursor-pointer items-center gap-1.5 text-sm"
                      >
                        <input
                          type="radio"
                          name="mergeSubModeEmpty"
                          value={value}
                          checked={mergeSubMode === value}
                          onChange={() => setMergeSubMode(value)}
                        />
                        {label}
                      </label>
                    ))}
                  </fieldset>
                </div>
              ) : (
                // MERGE_ACTIVE — sub-mode toggle + selected image cards
                <div
                  className={cn(
                    'flex w-full flex-col gap-4 transition-opacity duration-700',
                    mergeCollapseAnimate && 'pointer-events-none opacity-0',
                  )}
                >
                  {/* Sub-mode toggle */}
                  <fieldset className="flex gap-3">
                    <legend className="sr-only">Merge sub-mode</legend>
                    {(
                      [
                        { value: 'create-scene', label: 'Create Scene' },
                        { value: 'add-to-scene', label: 'Add to Scene' },
                      ] as { value: MergeSubMode; label: string }[]
                    ).map(({ value, label }) => (
                      <label
                        key={value}
                        className="flex cursor-pointer items-center gap-1.5 text-sm"
                      >
                        <input
                          type="radio"
                          name="mergeSubMode"
                          value={value}
                          checked={mergeSubMode === value}
                          onChange={() => handleMergeSubModeChange(value)}
                          aria-label={label}
                        />
                        {label}
                      </label>
                    ))}
                  </fieldset>

                  {mergeSubMode === 'add-to-scene' && (
                    <p className="text-xs text-gray-500">
                      The scene image appears as the background. Select a product card to set it as
                      the scene.
                    </p>
                  )}

                  {/* Image card grid (max 8, ordered by selection) */}
                  <div className="flex flex-wrap gap-3">
                    {mergeImages.map((url, idx) => {
                      const isScene =
                        mergeSubMode === 'add-to-scene' &&
                        (mergeSceneImageUrl === url || (!mergeSceneImageUrl && idx === 0));
                      return (
                        <div key={url} className="group relative flex flex-col items-center gap-1">
                          <div className="relative">
                            <img
                              src={url}
                              alt={`Product ${idx + 1}`}
                              className={cn(
                                'h-24 w-24 rounded-lg object-cover shadow',
                                isScene && 'ring-2 ring-violet-500',
                              )}
                            />
                            {/* Number badge */}
                            <span className="absolute top-1 left-1 flex h-5 w-5 items-center justify-center rounded-full bg-violet-600 text-[9px] font-bold text-white shadow">
                              {idx + 1}
                            </span>
                            {/* SCENE badge for add-to-scene */}
                            {isScene && (
                              <span className="absolute top-1 right-1 rounded bg-violet-600 px-1 py-0.5 text-[8px] font-bold uppercase tracking-widest text-white">
                                Scene
                              </span>
                            )}
                            {/* Hover ribbon */}
                            <div className="absolute inset-0 flex flex-col items-center justify-end gap-1 rounded-lg bg-black/0 pb-1 opacity-0 transition-all group-hover:bg-black/30 group-hover:opacity-100">
                              {mergeSubMode === 'add-to-scene' && !isScene && (
                                <button
                                  type="button"
                                  aria-label="Set as scene"
                                  onClick={() => handleSetAsSceneImage(url)}
                                  className="w-20 rounded bg-white/90 px-1 py-0.5 text-[9px] font-medium text-gray-800 hover:bg-white"
                                >
                                  Set as scene
                                </button>
                              )}
                              {mergeSubMode === 'add-to-scene' && isScene && (
                                <span className="w-20 rounded bg-violet-600/90 px-1 py-0.5 text-center text-[9px] font-medium text-white">
                                  Scene image
                                </span>
                              )}
                              <button
                                type="button"
                                aria-label={`Remove merge image ${idx + 1}`}
                                onClick={() => handleMergeImageToggle(url)}
                                className="w-20 rounded bg-white/90 px-1 py-0.5 text-[9px] font-medium text-red-600 hover:bg-white"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {/* "Add more" slot when fewer than MAX_MERGE_IMAGES */}
                    {mergeImages.length < MAX_MERGE_IMAGES && (
                      <div className="flex h-24 w-24 flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 text-gray-400 dark:border-gray-600">
                        <span className="text-xl">+</span>
                        <span className="text-[9px]">Add more</span>
                      </div>
                    )}
                  </div>

                  <p className="text-sm text-gray-500">
                    {mergeImages.length} image{mergeImages.length !== 1 ? 's' : ''} selected
                    {mergeImages.length < MERGE_MIN_IMAGES
                      ? ` — select ${MERGE_MIN_IMAGES - mergeImages.length} more to merge`
                      : ' — ready to merge'}
                  </p>
                </div>
              )
            ) : (
              // Default canvas (Enhance / Edit)
              <div className="group relative">
                <img
                  src={canvasImageUrl}
                  alt={product.title}
                  className="max-h-96 max-w-full rounded-2xl object-contain shadow-lg"
                  onLoad={(e) => {
                    const img = e.currentTarget;
                    if (img.naturalWidth && img.naturalHeight) {
                      setSrcImgDims({ w: img.naturalWidth, h: img.naturalHeight });
                    }
                  }}
                />
                <ImageBadge label={canvasBadge.label} variant={canvasBadge.variant} />
                {/* Zoom action — visible on hover (iter 77) */}
                <button
                  type="button"
                  aria-label="Zoom canvas image"
                  title="Zoom"
                  onClick={() => setZoomedImageUrl(canvasImageUrl)}
                  className="absolute top-2 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full bg-black/60 px-3 py-1 text-xs text-white opacity-0 backdrop-blur-sm transition-opacity duration-150 group-hover:opacity-100 hover:bg-black/80"
                >
                  <ZoomIn className="h-3.5 w-3.5" aria-hidden />
                  Zoom
                </button>
                {/* Floating lightbulb */}
                <button
                  type="button"
                  aria-label="Open inspiration"
                  onClick={() => setShowInspirationModal(true)}
                  className="absolute top-2 right-2 flex items-center justify-center rounded-full bg-white/80 p-2 text-amber-500 shadow hover:bg-white"
                >
                  <Lightbulb className="h-4 w-4" aria-hidden />
                </button>
              </div>
            )}
          </div>

          {/* ── Bottom prompt panel ──────────────────────────────────────────────────────────────────────────────────────── */}
          <form
            onSubmit={handleSubmit}
            className="shrink-0 border-t border-gray-100 bg-white p-4 dark:border-gray-800 dark:bg-gray-950"
          >
            {contentState === 'generating' ? (
              /* Generating state: rotating tips. Pool + cadence live in
               * `src/lib/processing-tips.ts` (8 s rotate / 300 ms fade). Mode-aware
               * via the `task` argument to `useRotatingTip` — iter 39. */
              <output
                aria-label="AI is generating"
                className="flex flex-col items-center gap-2 py-2 text-center"
              >
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  AI is creating your images…
                </p>
                <p
                  className="min-h-[1.25rem] text-xs text-gray-400 dark:text-gray-500"
                  style={{
                    opacity: tipVisible ? 1 : 0,
                    transition: `opacity ${TIP_FADE_MS}ms ease-in-out`,
                  }}
                >
                  {rotatingTip}
                </p>
              </output>
            ) : contentState === 'feedback' ? (
              /* Feedback state: thumbs-up / thumbs-down quality signal (Phase 2) */
              <output
                aria-label="Generation complete feedback"
                className="flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2.5 dark:bg-emerald-900/20"
              >
                <p className="flex-1 text-xs text-emerald-700 dark:text-emerald-300">
                  How did this turn out?
                </p>
                <button
                  type="button"
                  aria-label="Thumbs up"
                  onClick={() => handleFeedback('thumbs_up')}
                  className="rounded p-1 text-base leading-none hover:bg-emerald-100 dark:hover:bg-emerald-800"
                >
                  👍
                </button>
                <button
                  type="button"
                  aria-label="Thumbs down"
                  onClick={() => handleFeedback('thumbs_down')}
                  className="rounded p-1 text-base leading-none hover:bg-emerald-100 dark:hover:bg-emerald-800"
                >
                  👎
                </button>
                <button
                  type="button"
                  aria-label="Dismiss feedback"
                  onClick={() => setFeedbackDismissed(true)}
                  className="text-emerald-400 hover:text-emerald-600"
                >
                  ×
                </button>
              </output>
            ) : (
              /* Editing state: full prompt UI */
              <>
                {!(task === 'match-style' && matchLookState === 'COPY_EMPTY') && (
                  <Textarea
                    ref={promptRef}
                    placeholder={promptPlaceholder}
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    maxLength={PROMPT_MAX_LENGTH}
                    rows={2}
                    className="mb-2 w-full resize-none"
                  />
                )}
                {/* Prompt suggestion chips — iter 38. Visible only when the
                 * textarea is empty so they recede once the user is typing.
                 * Per-task table lives at module top (`PROMPT_SUGGESTIONS_BY_TASK`).
                 * Horizontal scroll keeps the row tidy at narrow widths. */}
                {!(task === 'match-style' && matchLookState === 'COPY_EMPTY') &&
                  prompt.trim().length === 0 && (
                    <div
                      aria-label="Prompt suggestions"
                      className="mb-3 flex gap-1.5 overflow-x-auto pb-0.5"
                    >
                      {PROMPT_SUGGESTIONS_BY_TASK[task].map((chip) => (
                        <button
                          key={chip}
                          type="button"
                          onClick={() => {
                            // Append a comma + space when there's already
                            // content (defensive — this branch only runs when
                            // prompt is empty); otherwise replace.
                            setPrompt(chip);
                            promptRef.current?.focus();
                          }}
                          className="shrink-0 rounded-pill border border-border-subtle px-3 py-1 text-xs text-fg-2 transition-colors hover:border-brand hover:text-brand"
                        >
                          {chip}
                        </button>
                      ))}
                    </div>
                  )}
                {/* Info banner: credit budget (spec verbatim strings) */}
                {creditsSkin !== 'neutralStandard' && (
                  <div className="mb-2 flex items-center gap-2">
                    <p className="text-xs text-amber-600">
                      {creditsSkin === 'danger' ? overBudgetMsg : 'Running low on credits.'}
                    </p>
                    {creditsSkin === 'danger' && !fittedBatch.blocked && (
                      <button
                        type="button"
                        aria-label="Fit to budget"
                        onClick={() => {
                          setSizeTier(fittedBatch.sizeTier);
                          setBatchSize(fittedBatch.batchSize);
                        }}
                        className="rounded px-1.5 py-0.5 text-xs font-medium text-violet-600 hover:bg-violet-50"
                      >
                        Fit to budget
                      </button>
                    )}
                  </div>
                )}
                {generateError && (
                  <p className="mb-2 text-xs text-red-500" role="alert">
                    {generateError}
                  </p>
                )}
                {feedbackError && (
                  <p className="mb-2 text-xs text-amber-600" role="alert">
                    {feedbackError}
                  </p>
                )}
                <div className="flex items-center justify-between gap-2">
                  {/* Left controls */}
                  <div className="flex items-center gap-2">
                    {/* Mode pill */}
                    <button
                      type="button"
                      aria-label={TASK_LABELS[task]}
                      aria-expanded={showModePopover}
                      onClick={() => setShowModePopover((v) => !v)}
                      className="flex items-center gap-1.5 rounded-full border border-gray-200 px-3 py-1 text-xs font-medium hover:border-violet-400"
                    >
                      {TASK_LABELS[task]}
                    </button>

                    {/* Prompt history popover trigger — iter 37. Clock glyph
                     * anchored right of the mode pill so the cluster reads as
                     * "current mode + recall past prompts". */}
                    <div className="relative">
                      <button
                        type="button"
                        aria-label="Prompt history"
                        aria-expanded={showPromptHistory}
                        onClick={() => setShowPromptHistory((v) => !v)}
                        className="flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:border-violet-400 hover:text-violet-600"
                      >
                        <Clock className="h-3.5 w-3.5" aria-hidden />
                      </button>
                      <PromptHistoryPopover
                        open={showPromptHistory}
                        entries={promptHistory}
                        onPick={(text) => {
                          setPrompt(text);
                          setShowPromptHistory(false);
                          promptRef.current?.focus();
                        }}
                        onClose={() => setShowPromptHistory(false)}
                      />
                    </div>

                    {/* Sub-mode pill — Match Look and Merge, otherwise style pill */}
                    {
                      task === 'match-style' ? (
                        <button
                          type="button"
                          aria-label={`Sub-mode: ${
                            matchLookSubMode === 'scene' ? 'Match Scene' : 'Match Style'
                          }`}
                          onClick={() => {
                            /* sub-mode picker lives on the canvas toggle */
                          }}
                          className="flex items-center gap-1 rounded-full border border-gray-200 px-2 py-1 text-[10px] text-gray-500 hover:border-violet-400"
                        >
                          {matchLookSubMode === 'scene' ? 'Match Scene' : 'Match Style'}
                        </button>
                      ) : task === 'merge' ? (
                        <button
                          type="button"
                          aria-label={`Merge sub-mode: ${mergeSubMode === 'create-scene' ? 'Create Scene' : 'Add to Scene'}`}
                          onClick={() => {
                            /* sub-mode toggle lives on the canvas */
                          }}
                          className="flex items-center gap-1 rounded-full border border-gray-200 px-2 py-1 text-[10px] text-gray-500 hover:border-violet-400"
                        >
                          {mergeSubMode === 'create-scene' ? 'Create Scene' : 'Add to Scene'}
                        </button>
                      ) : task === 'enhance' ? (
                        /* Two chips — photographer + photo style — open Enhance Settings */
                        <div className="relative flex items-center gap-1">
                          <button
                            type="button"
                            aria-label={`Photographer: ${photographer === 'express' ? 'Express' : 'Art Director'}`}
                            aria-expanded={showEnhanceSettings}
                            onClick={() => setShowEnhanceSettings((v) => !v)}
                            className="flex items-center gap-1 rounded-full border border-gray-200 px-2 py-1 text-[10px] text-gray-500 hover:border-violet-400"
                          >
                            {photographer === 'express' ? 'Express' : 'Art Director'}
                          </button>
                          <button
                            type="button"
                            aria-label={PHOTO_STYLE_LABELS[photoStyle]}
                            aria-expanded={showEnhanceSettings}
                            onClick={() => setShowEnhanceSettings((v) => !v)}
                            className="flex items-center gap-1 rounded-full border border-gray-200 px-2 py-1 text-[10px] text-gray-500 hover:border-violet-400"
                          >
                            {PHOTO_STYLE_LABELS[photoStyle]} ▾
                          </button>
                          <EnhanceSettingsPopover
                            open={showEnhanceSettings}
                            onClose={() => setShowEnhanceSettings(false)}
                            photographer={photographer}
                            onPhotographer={setPhotographer}
                            photoStyle={photoStyle}
                            onPhotoStyle={setPhotoStyle}
                          />
                        </div>
                      ) : null /* Edit mode — no style pill; AI follows instructions exactly */
                    }

                    {/* Output settings trigger (kebab) */}
                    <div className="relative">
                      <button
                        type="button"
                        aria-label="Output settings"
                        aria-expanded={showOutputSettings}
                        onClick={() => setShowOutputSettings((v) => !v)}
                        className="flex items-center justify-center rounded-full border border-gray-200 px-2 py-1 text-gray-500 hover:border-violet-400 hover:text-violet-600"
                      >
                        <MoreHorizontal className="h-4 w-4" aria-hidden />
                      </button>
                      <OutputSettingsPopover
                        open={showOutputSettings}
                        onClose={() => setShowOutputSettings(false)}
                        batchSize={batchSize}
                        onBatchSize={setBatchSize}
                        sizeTier={sizeTier}
                        onSizeTier={setSizeTier}
                        aspectRatio={aspectRatio}
                        onAspectRatio={setAspectRatio}
                        task={task}
                        totalCost={totalCost}
                      />
                    </div>
                  </div>

                  {/* Right controls */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">{totalCost} credits</span>
                    <button
                      type="submit"
                      aria-label="Generate"
                      disabled={
                        isGenerating ||
                        creditsSkin === 'danger' ||
                        (task === 'merge' && mergeImages.length < MERGE_MIN_IMAGES)
                      }
                      className="flex h-9 w-9 items-center justify-center rounded-full bg-violet-600 text-white shadow hover:bg-violet-700 disabled:opacity-50"
                    >
                      <ArrowRight className="h-4 w-4" aria-hidden />
                    </button>
                  </div>
                </div>
              </>
            )}
          </form>

          {/* Hidden file input for Match Look template upload */}
          <input
            ref={templateUploadRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleTemplateFileUpload}
          />

          {/* Mode popover */}
          {showModePopover && (
            <dialog
              open
              aria-label="Choose a mode"
              className="absolute bottom-full left-4 z-50 mb-2 w-72 rounded-xl border border-gray-200 bg-white p-4 shadow-xl dark:border-gray-700 dark:bg-gray-900"
            >
              <h2 className="mb-3 text-sm font-semibold">Edit Modes</h2>
              <div className="grid grid-cols-2 gap-2">
                {(Object.keys(TASK_LABELS) as GenerateTask[]).map((t) => (
                  <ModeTile
                    key={t}
                    task={t}
                    active={task === t}
                    onSelect={() => handleTaskChange(t)}
                  />
                ))}
              </div>
            </dialog>
          )}
        </main>
      </div>

      {/* ── Inspiration modal ──────────────────────────────────────────────────────────────────────────────────────────────── */}
      {showInspirationModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowInspirationModal(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setShowInspirationModal(false);
          }}
        >
          <dialog
            open
            aria-label="Prompt inspiration"
            aria-modal="true"
            className="relative w-80 rounded-2xl bg-white p-6 shadow-2xl dark:bg-gray-900"
          >
            <button
              type="button"
              aria-label="Close inspiration"
              onClick={() => setShowInspirationModal(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
            <h2 className="mb-4 text-base font-semibold">Need inspiration?</h2>
            {Object.entries(INSPIRATION_PRESETS).map(([category, presets]) => (
              <div key={category} className="mb-4">
                <h3 className="mb-2 text-xs font-medium text-gray-500">{category}</h3>
                <div className="flex flex-wrap gap-1.5">
                  {presets.map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => {
                        setPrompt(preset);
                        setShowInspirationModal(false);
                        promptRef.current?.focus();
                      }}
                      className="rounded-full border border-gray-200 px-2.5 py-1 text-xs hover:border-violet-400 hover:text-violet-600"
                    >
                      {preset}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </dialog>
        </div>
      )}

      {/* ── Compare modal (iter 80) ────────────────────────────────────────────
          Side-by-side INITIAL vs GENERATED with a draggable vertical divider.
          Left side = product's live primary image (source). Right side =
          selected draft. The split is a CSS clip-path on the GENERATED layer;
          the user drags the handle to reveal more of either side. */}
      {compareDraftUrl && (
        <dialog
          open
          aria-modal="true"
          aria-label="Compare original and generated"
          className="fixed inset-0 z-50 m-0 flex h-full max-h-none w-full max-w-none items-center justify-center bg-black/85 p-6"
        >
          <button
            type="button"
            aria-label="Close compare"
            onClick={() => setCompareDraftUrl(null)}
            className="absolute inset-0 cursor-default"
          />
          <div className="relative max-h-full max-w-full select-none">
            {/* INITIAL (source) — full bleed below */}
            <img
              src={getWixMediaSafeUrl(product.primaryImageUrl)}
              alt="Original"
              className="pointer-events-none block max-h-[80vh] max-w-[90vw] rounded-lg shadow-2xl"
            />
            {/* GENERATED (draft) — clipped to compareSplit % from the right */}
            <img
              src={compareDraftUrl}
              alt="Generated"
              className="pointer-events-none absolute inset-0 block h-full w-full rounded-lg object-contain"
              style={{ clipPath: `inset(0 0 0 ${compareSplit}%)` }}
            />
            {/* Labels */}
            <span className="absolute top-2 left-2 rounded bg-black/70 px-2 py-0.5 text-[10px] font-bold tracking-wider text-white uppercase">
              Original
            </span>
            <span className="absolute top-2 right-2 rounded bg-emerald-600 px-2 py-0.5 text-[10px] font-bold tracking-wider text-white uppercase">
              Generated
            </span>
            {/* Slider input — visible bar + draggable thumb. Native range
                input for keyboard a11y; styled as a thin vertical divider. */}
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              value={compareSplit}
              onChange={(e) => setCompareSplit(Number(e.target.value))}
              aria-label="Compare position"
              aria-valuetext={`${Math.round(compareSplit)} percent`}
              className="absolute inset-y-0 left-0 z-10 w-full cursor-ew-resize appearance-none bg-transparent"
              style={{ WebkitAppearance: 'none' }}
            />
            {/* Visible divider line + grab handle (pure-visual; the range
                input above captures pointer events). */}
            <div
              className="pointer-events-none absolute inset-y-0 w-0.5 bg-white shadow-lg"
              style={{ left: `${compareSplit}%`, transform: 'translateX(-1px)' }}
            >
              <div className="absolute top-1/2 left-1/2 flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-gray-900 shadow-lg">
                <SplitSquareVertical className="h-4 w-4" aria-hidden />
              </div>
            </div>
          </div>
          <button
            type="button"
            aria-label="Close compare"
            onClick={() => setCompareDraftUrl(null)}
            className="absolute top-4 right-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-xl text-gray-900 hover:bg-white"
          >
            ✕
          </button>
        </dialog>
      )}

      {/* ── Zoom modal (iter 77) ─────────────────────────────────────────────── */}
      {zoomedImageUrl && (
        <dialog
          open
          aria-modal="true"
          aria-label="Image preview"
          className="fixed inset-0 z-50 m-0 flex h-full max-h-none w-full max-w-none items-center justify-center bg-black/85 p-6"
        >
          {/* Backdrop click-to-close — its own focusable button so a keyboard
              user can dismiss via Tab + Enter. Escape also closes (handled
              in the global key handler). */}
          <button
            type="button"
            aria-label="Close preview"
            onClick={() => setZoomedImageUrl(null)}
            className="absolute inset-0 cursor-default"
          />
          <img
            src={zoomedImageUrl}
            alt="Full preview"
            className="pointer-events-none relative max-h-full max-w-full rounded-lg shadow-2xl"
          />
          <button
            type="button"
            aria-label="Close preview"
            onClick={() => setZoomedImageUrl(null)}
            className="absolute top-4 right-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-xl text-gray-900 hover:bg-white"
          >
            ✕
          </button>
        </dialog>
      )}
    </div>
  );
}
